import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function formatApiError(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Login failed"));
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(signupData.email, signupData.password, signupData.name);
      toast.success("Account created!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Signup failed"));
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md px-4"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <BookOpen className="w-8 h-8 text-nordic-blue" />
            <span className="font-heading text-2xl font-semibold text-nordic-text">Bildkort</span>
          </Link>
          <p className="text-nordic-text-secondary text-sm">Sign in to save your progress across devices</p>
        </div>

        <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 shadow-sm p-8">
          {/* Google Login */}
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full rounded-xl border-nordic-wood/30 py-6 text-base font-medium mb-6 hover:bg-nordic-muted"
            style={{ transition: "background-color 0.2s ease" }}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-nordic-wood/20" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-nordic-paper px-3 text-nordic-text-muted">or</span>
            </div>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-nordic-muted rounded-xl">
              <TabsTrigger value="login" className="rounded-lg text-sm" data-testid="login-tab">Log In</TabsTrigger>
              <TabsTrigger value="signup" className="rounded-lg text-sm" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-sm text-nordic-text-secondary">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nordic-text-muted" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="pl-10 rounded-xl bg-nordic-bg border-nordic-wood/30"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-sm text-nordic-text-secondary">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nordic-text-muted" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="pl-10 rounded-xl bg-nordic-bg border-nordic-wood/30"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full py-5 text-base font-medium"
                  style={{ transition: "background-color 0.3s ease" }}
                  data-testid="login-submit-btn"
                >
                  {loading ? "Logging in..." : "Log In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <Label htmlFor="signup-name" className="text-sm text-nordic-text-secondary">Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nordic-text-muted" />
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Your name"
                      value={signupData.name}
                      onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                      className="pl-10 rounded-xl bg-nordic-bg border-nordic-wood/30"
                      required
                      data-testid="signup-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="signup-email" className="text-sm text-nordic-text-secondary">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nordic-text-muted" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signupData.email}
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                      className="pl-10 rounded-xl bg-nordic-bg border-nordic-wood/30"
                      required
                      data-testid="signup-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="signup-password" className="text-sm text-nordic-text-secondary">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nordic-text-muted" />
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="Choose a password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      className="pl-10 rounded-xl bg-nordic-bg border-nordic-wood/30"
                      required
                      minLength={8}
                      data-testid="signup-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-nordic-blue text-white hover:bg-nordic-blue/90 rounded-full py-5 text-base font-medium"
                  style={{ transition: "background-color 0.3s ease" }}
                  data-testid="signup-submit-btn"
                >
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-xs text-nordic-text-muted mt-6">
          Continue without an account — <Link to="/" className="text-nordic-blue hover:underline">explore freely</Link>
        </p>
      </motion.div>
    </div>
  );
}
